import { Component, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash-es';
import {
  Workflow,
  WorkflowParentType,
  WorkflowRuleEvent,
  WorkflowRuleImplementation,
  WorkflowStep,
  WorkflowStepRule,
  WorkflowStepRuleAction
} from 'src/app/modules/core/store/models/workflow.model';
import * as fromWorkflow from 'src/app/modules/communications/store/workflow';
import { TranslateService } from '@ngx-translate/core';
import { FieldDataType, FilterGroup } from 'src/app/modules/core/store/models';
import {
  DateRuleConfiguration,
  DateRuleOffsetDir,
  DateRuleOffsetType,
  DateRuleSourceType,
  RuleConfiguration
} from './workflow-rule-edit.model';
import { WorkflowRuleActionEditDirective } from '../workflow-rule-action-edit.directive';
import { FilterDisplayComponent } from 'src/app/modules/core/components/filters/filter-display/filter-display.component';
import { WorkflowRuleActionsMap } from '../workflow-rule-actions.map';
import moment from 'moment';
import { WorkflowRuleDataField } from 'src/app/modules/communications/store/workflow/workflow.models';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-workflow-rule-edit',
    templateUrl: './workflow-rule-edit.component.html',
    styleUrls: ['./workflow-rule-edit.component.scss'],
    standalone: false
})
export class WorkflowRuleEditComponent implements OnInit, OnDestroy {
  @ViewChild('picker') picker: any;
  @ViewChild('filter') filter: FilterDisplayComponent;

  @ViewChildren(WorkflowRuleActionEditDirective) actionControls;

  isActive: boolean;
  width: string = '800';
  rule: WorkflowStepRule;
  step: WorkflowStep;
  editActionUId?: string;
  isNewAction: boolean;
  isNew: boolean;
  showActionsUId: boolean;

  selectedOffsetValue: any;
  selectedOffsetType: DateRuleOffsetType;
  selectedOffsetDir: DateRuleOffsetDir;
  selectedOffsetSource: string;
  selectedOffsetDate: string;
  showDeleteConfirmation: boolean;
  filterGroup: FilterGroup;
  workflow: Workflow;
  schema: WorkflowRuleDataField[];
  ruleImplementations?: any[];
  selectedRuleEvent: WorkflowRuleEvent;
  actionToDelete: WorkflowStepRuleAction;

  destroy$: Subject<void> = new Subject();

  ruleEvents = [
    { id: WorkflowRuleEvent.StepStarted, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-EVENT-STEPSTARTED'), title: null },
    { id: WorkflowRuleEvent.StepStopped, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-EVENT-STEPSTOPPED'), title: null },
    {
      id: WorkflowRuleEvent.DocumentUpdated,
      name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-EVENT-DOCUPDATED'),
      title: null
    },
    {
      id: WorkflowRuleEvent.SpecificTime,
      name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-EVENT-SPECIFICTIME'),
      title: this.translationService.instant('MANAGEMENT.WORKFLOW.DEFINE-SPECIFIC-CRITERIA')
    },
    {
      id: WorkflowRuleEvent.AfterTime,
      name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-EVENT-AFTERTIME'),
      title: this.translationService.instant('MANAGEMENT.WORKFLOW.DEFINE-PERIOD-CRITERIA')
    },
    {
      id: WorkflowRuleEvent.Scheduled,
      name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-EVENT-SCHEDULED'),
      title: this.translationService.instant('MANAGEMENT.WORKFLOW.DEFINE-SCHEDULED-CRITERIA')
    }
  ];

  offsetTypes = [
    { id: DateRuleOffsetType.Minutes, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-6') },
    { id: DateRuleOffsetType.Hours, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-1') },
    { id: DateRuleOffsetType.Days, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-2') },
    { id: DateRuleOffsetType.Weeks, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-3') },
    { id: DateRuleOffsetType.Months, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-4') },
    { id: DateRuleOffsetType.Years, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-5') }
  ];

  offsetDirs = [
    { id: DateRuleOffsetDir.after, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-AFTER') },
    { id: DateRuleOffsetDir.before, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-BEFORE') }
  ];

  offsetSources: any[];

  constructor(
    private store: Store<any>,
    private workflowStore: Store<fromWorkflow.State>,
    private notify: SystemNotificationsService,
    private communicationsService: CommunicationsApiService,
    private translationService: TranslateService
  ) {}

  ngOnInit(): void {
    this.workflowStore
      .select(fromWorkflow.selectSelectedWorkflowStepRule)
      .pipe(withLatestFrom(this.workflowStore.select(fromWorkflow.selectSelectedWorkflowStep)), takeUntil(this.destroy$))
      .subscribe(([rule, step]) => {
        this.step = step ? cloneDeep(step) : null;
        this.rule = rule ? cloneDeep(rule) : null;
        this.isActive = rule != null;
        this.editActionUId = null;

        if (rule) {
          this.selectedRuleEvent = rule.ruleEvent;
          this.loadRuleSettings();

          if (step) {
            this.isNew = !step.rules?.find(r => r.uId === rule.uId);
            if (this.isNew) {
              this.addActionClick();
            }
          }
        }
      });

    this.workflowStore
      .select(fromWorkflow.selectRuleImplementations)
      .pipe(takeUntil(this.destroy$))
      .subscribe(impls => {
        this.ruleImplementations = impls;
        if (impls?.length == 0) {
          this.workflowStore.dispatch(fromWorkflow.loadWorkflowRuleImplementations({ parentType: this.workflow?.parentType }));
        } else {
          this.loadImplementations(impls);
        }
      });

    this.store
      .select(fromWorkflow.selectSelectedWorkflow)
      .pipe(takeUntil(this.destroy$))
      .subscribe(workflow => {
        if (workflow) {
          this.workflow = workflow;
          if (!!workflow) {
            if (workflow?.parentType === WorkflowParentType.MassNotification) {
              this.communicationsService.getMassNotificationRuleDataFields().subscribe(res => {
                this.schema = [];
                res?.forEach(r => {
                  this.schema.push({
                    systemName: r.systemName,
                    dataType: r.dataType,
                    format: r.format,
                    displayName: this.translationService.instant('COMMUNICATIONS.FIELDS.' + r.displayName)
                  });
                });
              });
            } else {
              this.communicationsService.getMassNotificationRecipientRuleDataFields().subscribe(res => {
                this.schema = [];
                res?.forEach(r => {
                  this.schema.push({
                    systemName: r.systemName,
                    dataType: r.dataType,
                    format: r.format,
                    displayName: this.translationService.instant('COMMUNICATIONS.FIELDS.' + r.displayName)
                  });
                });
              });
            }
          }
          this.workflowStore.dispatch(fromWorkflow.loadWorkflowRuleImplementations({ parentType: this.workflow?.parentType }));
          this.loadOffsetSources();
          if (!!this.rule) {
            this.loadRuleSettings();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isDateCriteria(): boolean {
    return (
      this.selectedRuleEvent === WorkflowRuleEvent.AfterTime ||
      this.selectedRuleEvent === WorkflowRuleEvent.SpecificTime ||
      this.selectedRuleEvent === WorkflowRuleEvent.Scheduled
    );
  }

  get dateCriteriaLabel(): string {
    return this.ruleEvents.find(e => e.id == this.selectedRuleEvent).title;
  }
  get isSpecificTime(): boolean {
    return this.selectedRuleEvent === WorkflowRuleEvent.SpecificTime;
  }

  get isSchedules(): boolean {
    return this.selectedRuleEvent === WorkflowRuleEvent.Scheduled;
  }

  get isAfterTime(): boolean {
    return this.selectedRuleEvent === WorkflowRuleEvent.AfterTime;
  }

  get canSave(): boolean {
    return this.rule?.actions.length > 0 && this.rule?.actions[0].implementationUId !== null;
  }

  get canActionCancel(): boolean {
    return this.rule?.actions.length > 1 && this.rule?.actions[0].implementationUId !== null;
  }

  private loadOffsetSources(): void {
    /*    this.offsetSources = [];
    this.dataset.schema.fields?.filter(f => f.dataType === FieldDataType.DateTime)
      .map(f => this.offsetSources.push({ id: f.systemName, name: f.displayName }));
    this.offsetSources.push({ id: 'step_start_date', name: this.translationService.instant("MANAGEMENT.WORKFLOW.RULE-SOURCE-STEP") });*/
  }

  private loadImplementations(impls: WorkflowRuleImplementation[]): void {
    this.ruleImplementations = [];

    if (impls.length) {
      this.ruleImplementations.push({ uId: null, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-SELECT-ACTION') });
      impls.map(i => {
        /*        // Execute pipeline action only applicable to inicident type datasets
        if (i.uId == "fad5aef3-0e0c-4c0b-86c4-dd171f6ea42b" && (!this.dataset || this.dataset.type != DatasetType.Incident)) {
          return;
        }
*/
        const implRecord = WorkflowRuleActionsMap.get(i.uId);
        this.ruleImplementations.push({ uId: i.uId, name: implRecord ? this.translationService.instant(implRecord.desc) : i.name });
      });
    }
  }

  private loadRuleSettings(): void {
    let settings = this.rule?.configuration ? (JSON.parse(this.rule.configuration) as DateRuleConfiguration) : null;

    this.filterGroup = null;
    this.selectedOffsetType = DateRuleOffsetType.Days;
    this.selectedOffsetDir = DateRuleOffsetDir.after;
    this.selectedOffsetValue = '0';
    this.selectedOffsetSource = 'step_start_date';
    this.selectedOffsetDate = null;

    if (settings) {
      this.filterGroup = settings.filterGroup;
      if (this.isDateCriteria) {
        const offsetValue = !settings.dateOffset || isNaN(settings.dateOffset) ? 0 : settings.dateOffset;
        this.selectedOffsetType =
          settings.offsetType === undefined || settings.offsetType === 0 ? DateRuleOffsetType.Days : settings.offsetType;
        this.selectedOffsetDir = offsetValue >= 0 ? DateRuleOffsetDir.after : DateRuleOffsetDir.before;
        this.selectedOffsetValue = offsetValue >= 0 ? offsetValue.toString() : -offsetValue.toString();
        this.selectedOffsetSource = settings.sourceType === DateRuleSourceType.DocumentField ? settings.fieldName : 'step_start_date';
        this.selectedOffsetDate = settings.specificDate ? settings.specificDate : null;
      }
    }
  }

  private buildRuleDisplayText(): void {
    let eventName = '';

    const ruleEvent = this.ruleEvents?.find(e => e.id === this.rule?.ruleEvent);
    if (!!ruleEvent) {
      eventName = `<b>${ruleEvent.name}</b> `;
    }

    let ruleDisplayText = eventName;

    if (this.isDateCriteria) {
      if (this.isAfterTime) {
        const offsetName = this.offsetTypes.find(o => o.id === this.selectedOffsetType)?.name;
        const dirName = this.offsetDirs.find(o => o.id === this.selectedOffsetDir)?.name;
        const sourceName = this.offsetSources.find(o => o.id === this.selectedOffsetSource)?.name;
        const desc = `${this.selectedOffsetValue} ${offsetName} ${dirName} ${sourceName}`;
        ruleDisplayText += this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-DESC-AFTER', { desc: desc });
      } else if (this.isSchedules) {
        const offsetName = this.offsetTypes.find(o => o.id === this.selectedOffsetType)?.name;
        const desc = `${this.selectedOffsetValue} ${offsetName}`;
        ruleDisplayText += this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-DESC-SCHEDULED', { desc: desc });
      } else if (this.isSpecificTime) {
        if (this.selectedOffsetDate != null) {
          const timeFormat = this.translationService.instant('FORMAT.DATE-ONLY');
          const dt = moment(this.selectedOffsetDate);
          const formatedDate = dt.format(timeFormat);
          ruleDisplayText += this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-DESC-SPECIFIC', { desc: formatedDate });
        }
      }
    }

    if (this.filterGroup !== null) {
      ruleDisplayText += ', ' + this.translationService.instant('MANAGEMENT.WORKFLOW.FILTER-CONDITIONS') + ' (';
      ruleDisplayText += this.filter.filterDisplayText;
      ruleDisplayText += ') ';
    }

    if (this.rule?.actions?.length) {
      const actionText = [];
      this.rule?.actions?.map(a => actionText.push(a.displayText));
      ruleDisplayText += ', ' + actionText.join(' , ');
    }
    this.rule.displayText = ruleDisplayText;
  }

  ruleEventChanged($event): void {
    if (this.isDateCriteria) {
      this.loadRuleSettings();
    }
  }

  close(): void {
    this.workflowStore.dispatch(fromWorkflow.clearSelectedWorkflowStepRule());
  }
  cancelClick(): void {
    this.close();
  }

  saveClick(): void {
    if (this.editActionUId !== null) {
      if (!this.saveActionClick(null)) {
        return;
      }
    }

    if (this.rule?.actions.length === 0) {
      this.workflowStore.dispatch(fromWorkflow.clearSelectedWorkflowStepRule());
      return;
    }

    const indexToRemove = this.rule.actions.findIndex(ws => ws.implementationUId === null);
    if (indexToRemove >= 0) {
      this.rule.actions.splice(indexToRemove, 1);
    }

    this.rule.ruleEvent = this.selectedRuleEvent;

    const sourceType = this.selectedOffsetSource === 'step_start_date' ? DateRuleSourceType.StepStart : DateRuleSourceType.DocumentField;

    let dateOffset = parseInt(this.selectedOffsetValue);

    if (isNaN(dateOffset)) {
      if (this.selectedRuleEvent === WorkflowRuleEvent.AfterTime) {
        this.notify.error(
          this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ERROR-BAD-NUMBER', {
            value: this.selectedOffsetValue === undefined ? '' : this.selectedOffsetValue
          })
        );
        return;
      } else {
        dateOffset = 0;
      }
    }

    if (isNaN(dateOffset) || dateOffset <= 0) {
      if (this.selectedRuleEvent === WorkflowRuleEvent.Scheduled) {
        this.notify.error(
          this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ERROR-BAD-NUMBER', {
            value: this.selectedOffsetValue === undefined ? '' : this.selectedOffsetValue
          })
        );
        return;
      }
    }

    var date = moment(this.selectedOffsetDate);
    if (this.selectedRuleEvent === WorkflowRuleEvent.SpecificTime) {
      if (!date.isValid()) {
        this.notify.error(
          this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ERROR-BAD-DATE', {
            value: this.selectedOffsetDate === undefined ? '' : this.selectedOffsetDate
          })
        );
        return;
      }
    } else {
      this.selectedOffsetDate = '';
    }

    this.buildRuleDisplayText();

    if (this.isDateCriteria) {
      let settings = {
        filterGroup: this.filterGroup,
        sourceType: sourceType,
        offsetType: this.selectedOffsetType,
        dateOffset: this.selectedOffsetDir === DateRuleOffsetDir.after ? dateOffset : -dateOffset,
        specificDate: this.selectedOffsetDate,
        fieldName: sourceType === DateRuleSourceType.DocumentField ? this.selectedOffsetSource : null
      } as DateRuleConfiguration;

      this.rule.configuration = JSON.stringify(settings);
    } else {
      let settings = {
        filterGroup: this.filterGroup
      } as RuleConfiguration;

      this.rule.configuration = JSON.stringify(settings);
    }

    this.workflowStore.dispatch(fromWorkflow.saveWorkflowStepRule({ step: this.step, rule: this.rule }));
  }

  addActionClick(): void {
    this.ruleImplementations = this.ruleImplementations.filter(i => i.uId !== null);
    this.ruleImplementations.unshift({ uId: null, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-SELECT-ACTION') });

    const newAction = {
      uId: uuidv4(),
      implementationUId: this.ruleImplementations?.[0].uId,
      sortOrder: this.rule?.actions.length + 1
    } as WorkflowStepRuleAction;

    let updatedRule = cloneDeep(this.rule);

    updatedRule.actions.push(newAction);

    this.editActionUId = newAction.uId;

    let sortOrder = 0;
    updatedRule.actions.map(a => (a.sortOrder = sortOrder++));

    this.rule = updatedRule;
    this.isNewAction = true;
  }

  editActionClick(action): void {
    if (this.editActionUId) {
      return;
    }
    this.editActionUId = action.uId;
    this.isNewAction = false;
    this.ruleImplementations = this.ruleImplementations.filter(i => i.uId !== null);
  }

  deleteActionClick(action): void {
    this.actionToDelete = action;
    this.showDeleteConfirmation = true;
  }

  onConfirmDeleteAction(confirm): void {
    if (confirm) {
      this.deleteAction();
    }
    this.showDeleteConfirmation = false;
  }

  private deleteAction(): void {
    const indexToRemove = this.rule.actions.findIndex(ws => ws.uId === this.actionToDelete.uId);
    if (indexToRemove >= 0) {
      this.rule.actions.splice(indexToRemove, 1);
    }

    let sortOrder = 0;
    this.rule.actions.map(a => (a.sortOrder = sortOrder++));
  }

  cancelActionClick(action): void {
    if (this.isNewAction) {
      this.actionToDelete = action;
      this.deleteAction();
    }
    this.editActionUId = null;
  }

  saveActionClick(action): boolean {
    const actionControl = this.actionControls._results.find(c => c._action.uId === this.editActionUId);
    if (actionControl) {
      if (!actionControl.save()) {
        return false;
      }
    }
    this.editActionUId = null;
    return true;
  }

  actionDrop(dragDropEvent: any): void {
    const [actionToMove] = this.rule.actions.splice(dragDropEvent.previousIndex, 1);
    this.rule.actions.splice(dragDropEvent.currentIndex, 0, actionToMove);

    let sortOrder = 0;
    this.rule.actions.map(a => (a.sortOrder = sortOrder++));
  }

  filterChanged(updatedFilterGroup: FilterGroup) {
    this.filterGroup = updatedFilterGroup;
  }
}
